<template>
    <div class="circle">
        <div role="progressbar" :aria-valuenow="value" :aria-valuemin="min" :aria-valuemax="max"
            :style="{ '--value': visualPercentage, '--primary': progressColor }">
            <!-- Centered text container inside the circle -->
            <div class="circle-text">
                <!-- Show value inside circle if not only percentage mode -->
                <span v-if="!showPercentage || showPercentageHint" class="circle-value">
                    {{ value }}
                </span>

                <!-- Show percentage as hint below the value -->
                <span v-if="showPercentageHint" class="circle-percentage">
                    {{ Math.round(percentage) }}%
                </span>

                <!-- Show percentage only if showPercentage true and not hint mode -->
                <span v-else-if="showPercentage" class="circle-value">
                    {{ Math.round(percentage) }}%
                </span>
            </div>
        </div>

        <p class="text-center">
            <slot></slot>
        </p>
    </div>
</template>


<script setup>
import { computed } from 'vue'

const props = defineProps({
    min:                { type: Number, default: 0 },               // lowest score
    max:                { type: Number, default: 100 },             // highest score
    value:              { type: [Number, String], required: true }, // value
    showPercentage:     { type: Boolean, default: false },          // show percentage
    showPercentageHint: { type: Boolean, default: false },          // show value with percentage below
    allowOverflowColor: { type: Boolean, default: false },          // overflow visual colors
})

const safeValue = computed(() => {
    return typeof props.value === 'number' ? props.value : props.min
})

// Compute percentage
const percentage = computed(() => {
    const range = props.max - props.min
    if (range <= 0) return 0
    return ((safeValue.value - props.min) / range) * 100
})

const visualPercentage = computed(() => {
    const range = props.max - props.min
    if (range <= 0) return 0
    return ((safeValue.value - props.min) / range) * 100
})

function rgbToHsl({ r, g, b }) {
    r /= 255; g /= 255; b /= 255
    const max = Math.max(r, g, b), min = Math.min(r, g, b)
    let h = 0, s = 0, l = (max + min) / 2

    if (max !== min) {
        const d = max - min
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
        switch (max) {
            case r: h = (g - b) / d + (g < b ? 6 : 0); break
            case g: h = (b - r) / d + 2; break
            case b: h = (r - g) / d + 4; break
        }
        h /= 6
    }
    return { h, s, l }
}

function hslToRgb({ h, s, l }) {
    let r, g, b

    if (s === 0) {
        r = g = b = l
    } else {
        const hue2rgb = (p, q, t) => {
            if (t < 0) t += 1
            if (t > 1) t -= 1
            if (t < 1 / 6) return p + (q - p) * 6 * t
            if (t < 1 / 2) return q
            if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6
            return p
        }
        const q = l < 0.5 ? l * (1 + s) : l + s - l * s
        const p = 2 * l - q
        r = hue2rgb(p, q, h + 1 / 3)
        g = hue2rgb(p, q, h)
        b = hue2rgb(p, q, h - 1 / 3)
    }
    return { r: Math.round(r * 255), g: Math.round(g * 255), b: Math.round(b * 255) }
}

// Compute color from red → yellow → green
const progressColor = computed(() => {
    let rawP = 0
    if (typeof props.value === 'number') {
        rawP = Math.max(0, (props.value - props.min) / (props.max - props.min))
    }

    const cappedP = props.allowOverflowColor ? Math.min(rawP, 2) : Math.min(rawP, 1)

    // Key colors in HSL
    const red    = rgbToHsl({ r: 210, g: 33,  b: 14 })  // #d2210e
    const yellow = rgbToHsl({ r: 242, g: 151, b: 4 })   // #f29704
    const green  = rgbToHsl({ r: 0,   g: 150, b: 20 })  // #009614
    const blue   = rgbToHsl({ r: 0,   g: 91,  b: 236 }) // #005bec
    const purple = rgbToHsl({ r: 109, g: 0,   b: 236 }) // #6d00ec

    let h, s, l

    if (!props.allowOverflowColor) {
        // simple red → yellow → green
        if (cappedP <= 0.3) {
            const t = cappedP / 0.3
            h = red.h + (yellow.h - red.h) * t
            s = red.s + (yellow.s - red.s) * t
            l = red.l + (yellow.l - red.l) * t
        } else {
            const t = (cappedP - 0.3) / 0.7
            h = yellow.h + (green.h - yellow.h) * t
            s = yellow.s + (green.s - yellow.s) * t
            l = yellow.l + (green.l - yellow.l) * t
        }
    } else {
        // red → yellow → green → blue → purple (your original logic)
        if (cappedP <= 0.3) {
            const t = cappedP / 0.3
            h = red.h + (yellow.h - red.h) * t
            s = red.s + (yellow.s - red.s) * t
            l = red.l + (yellow.l - red.l) * t
        } else if (cappedP <= 0.495) {
            const t = (cappedP - 0.3) / 0.495
            h = yellow.h + (green.h - yellow.h) * t
            s = yellow.s + (green.s - yellow.s) * t
            l = yellow.l + (green.l - yellow.l) * t
        } else if (cappedP <= 0.75) {
            h = green.h; s = green.s; l = green.l
        } else if (cappedP <= 0.85) {
            const t = (cappedP - 0.75) / 0.85
            h = green.h + (blue.h - green.h) * t
            s = green.s + (blue.s - green.s) * t
            l = green.l + (blue.l - green.l) * t
        } else if (cappedP <= 1) {
            const t = (cappedP - 0.85) / 0.15
            h = blue.h + (purple.h - blue.h) * t
            s = blue.s + (purple.s - blue.s) * t
            l = blue.l + (purple.l - blue.l) * t
        } else {
            h = purple.h; s = purple.s; l = purple.l
        }
    }

    const { r, g, b } = hslToRgb({ h, s, l })
    return `rgb(${r}, ${g}, ${b})`
})
</script>

<style lang="scss" scoped>
@keyframes progress {
    0% {
        --percentage: 0;
    }

    100% {
        --percentage: var(--value);
    }
}

@property --percentage {
    syntax: '<number>';
    inherits: true;
    initial-value: 0;
}

[role="progressbar"] {
    --percentage: var(--value);
    --primary: #369;
    --secondary: #adf;
    --size: 10rem;
    animation: progress 2s 0.5s forwards;
    width: var(--size);
    aspect-ratio: 1;
    border-radius: 50%;
    position: relative;
    overflow: hidden;
    display: grid;
    place-items: center;
    transition: --primary 0.5s ease; // smooth color change
}

[role="progressbar"]::before {
    content: "";
    position: absolute;
    inset: 0;
    background: conic-gradient(var(--primary) calc(var(--percentage) * 1%), var(--secondary) 0);
    mask: radial-gradient(white 55%, transparent 0);
    -webkit-mask: radial-gradient(#0000 55%, #000 0);
    transition: background 0.5s ease;
}

[role="progressbar"]::after {
    content: var(--display);
    font-family: Helvetica, Arial, sans-serif;
    font-size: calc(var(--size) / 5);
    color: var(--primary);
    transition: color 0.5s ease;
}

.circle {
    display: flex;
    flex-direction: column;
    align-items: center;
}

.circle-text {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
}

.circle-value {
    font-size: calc(var(--size) / 5); /* main value size */
    color: var(--primary);
}

.circle-percentage {
    font-size: calc(var(--size) / 10); /* smaller than main value */
    color: var(--primary);
    margin-top: 0.1rem;
}

</style>
